import React from 'react';
import io from 'socket.io-client';
import { overlayScale } from './leagueOverlay';

// Torneos de eliminatoria (apps/torneos.js): el cuadro, la pagina /info/torneos y el overlay
// de OBS, que pinta el torneo marcado en el panel (Torneos -> "Mostrar en overlay"):
//   https://forallia.com/?streamGadgets=torneo   (&scale=1.4 para el ajuste fino del tamaño)
// Sin ninguno marcado no pinta nada, para poder dejar la fuente siempre puesta en la escena.

const roundName = (r, total) => ['Campeón', 'Final', 'Semifinales', 'Cuartos'][total - 1 - r] || 'Ronda ' + (r + 1);

// Gana quien ya esta en la ronda siguiente: en verde, y su rival en rojo.
const advanced = (rounds, r, name) => !!name && r < rounds.length - 1 && rounds[r + 1].includes(name);

function slotState(rounds, r, i) {
    const name = rounds[r][i];
    if (r === rounds.length - 1) return name ? 'win' : '';
    if (advanced(rounds, r, name)) return 'win';
    if (name && advanced(rounds, r, rounds[r][i ^ 1])) return 'lose';
    return '';
}

// Con onWinner/onUndo/onSlot (panel) se pulsa un nombre para darle el partido, la ↶ del ganador lo
// deshace y los huecos de la primera ronda sin jugar se rellenan escribiendo o se vacian con la ×.
const Slot = ({ rounds, r, i, onWinner, onUndo, onSlot }) => {
    const name = rounds[r][i];
    const state = slotState(rounds, r, i);
    const last = r === rounds.length - 1;
    const won = state === 'win' && !last;
    const editable = onSlot && r === 0 && !advanced(rounds, 0, name) && !advanced(rounds, 0, rounds[0][i ^ 1]);
    return (
        <div className={'bracketSlot ' + state}>
            {!name && editable
                ? <input className='bracketInput' placeholder='Participante' maxLength={40}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    onBlur={(e) => { if (e.currentTarget.value.trim()) onSlot(i, e.currentTarget.value); }} />
                : name && onWinner && !last && !won
                    ? <button className='bracketName' title='Darle el partido' onClick={() => onWinner(r, i)}>{name}</button>
                    : <span className='bracketName'>{name || '—'}</span>}
            {name && editable ? <button className='bracketRemove' title='Quitar del cuadro' onClick={() => onSlot(i, '')}>×</button> : ''}
            {won && onUndo ? <button className='bracketRemove bracketUndo' title='Deshacer su victoria' aria-label='Deshacer su victoria' onClick={() => onUndo(r, i)}>↶</button> : ''}
        </div>
    );
};

export const Bracket = ({ rounds, onWinner, onUndo, onSlot }) => (
    <div className='bracket'>
        {rounds.map((round, r) => (
            <div className='bracketRound' key={r}>
                <div className='bracketRoundName'>{roundName(r, rounds.length)}</div>
                <div className='bracketMatches'>
                    {/* El campeon es un "partido" de un solo hueco. */}
                    {(round.length === 1 ? [0] : round.map((n, i) => i).filter(i => i % 2 === 0)).map(i => (
                        <div className='bracketMatch' key={i}>
                            <Slot rounds={rounds} r={r} i={i} onWinner={onWinner} onUndo={onUndo} onSlot={onSlot} />
                            {round.length > 1 ? <Slot rounds={rounds} r={r} i={i + 1} onWinner={onWinner} onUndo={onUndo} onSlot={onSlot} /> : ''}
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
);

// Lee los torneos y los vuelve a leer con cada cambio del panel (y tras reconectar, por si se
// perdio algun aviso).
function useTorneos(url) {
    const [data, setData] = React.useState(undefined);
    React.useEffect(() => {
        const load = () => fetch(url).then(res => res.json()).then(setData)
            .catch(err => console.error('Error cargando los torneos:', err));
        load();
        const socket = io('https://forallia.com');
        socket.on('torneos', load);
        socket.on('connect', load);
        return () => { socket.disconnect(); };
    }, [url]);
    return data;
}

export const TorneoOverlay = () => {
    const torneo = useTorneos('/api/torneos?overlay=1');

    // Como en leagueOverlay: html tiene fondo propio en App.css y aqui tiene que verse la escena.
    React.useEffect(() => {
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
    }, []);

    if (!torneo) return null;
    return (
        <div className='torneoOverlay heroes'
            style={{ '--overlayScale': overlayScale, '--slots': torneo.rounds[0].length, '--rounds': torneo.rounds.length }}>
            <div className='torneoOverlayTitle'>{torneo.name}</div>
            <Bracket rounds={torneo.rounds} />
        </div>
    );
};

// Los torneos por juego: fila de juegos (con su imagen, como las ligas de /games), fila de torneos
// de ese juego y su cuadro. Lo usan /info/torneos y la seccion Torneos de /games. Los que no
// tienen juego van juntos en "Otros".
export const TorneosBrowser = () => {
    const torneos = useTorneos('/api/torneos');
    const [gameSel, setGameSel] = React.useState(null);
    const [selected, setSelected] = React.useState(null);

    if (!torneos) return <p>Cargando...</p>;
    if (torneos.length === 0) return <p>Todavía no hay ningún torneo.</p>;
    // Llegan del mas reciente al mas antiguo: el primer juego es el del ultimo torneo.
    const byGame = torneos.reduce((acc, t) => {
        const key = t.game ? t.game.name : 'Otros';
        (acc[key] = acc[key] || []).push(t);
        return acc;
    }, {});
    const gameNames = Object.keys(byGame);
    const game = byGame[gameSel] ? gameSel : gameNames[0];
    const torneo = byGame[game].find(t => t._id === selected) || byGame[game][0];
    return (
        <>
            {gameNames.length > 1 || torneo.game ? (
                <div className='navTops heroes unselectable'>
                    {gameNames.map(name => {
                        const image = byGame[name][0].game && byGame[name][0].game.image;
                        return (
                            <div key={name} className={name === game ? 'active' : ''} onClick={() => { setGameSel(name); setSelected(null); }}>
                                {image ? <img className='gameImg' src={image} alt={name.replace(/_/g, ' ')} /> : name.replace(/_/g, ' ')}
                            </div>
                        );
                    })}
                </div>
            ) : ''}
            <div className='navTops heroes unselectable'>
                {byGame[game].map(t => (
                    <div key={t._id} className={t === torneo ? 'active' : ''} onClick={() => setSelected(t._id)}>{t.name}</div>
                ))}
            </div>
            <Bracket rounds={torneo.rounds} />
        </>
    );
};

const Torneos = () => (
    <div className='page'>
        <div className='topTickets torneosPage'>
            <h1>Torneos</h1>
            <TorneosBrowser />
        </div>
    </div>
);

export default Torneos;
